import { resolveDynamicVariables } from "@/lib/replaceDynamicValues";
import { SITE_CONFIG } from "@/constants/siteConfig";

// Guards for JSON-LD that originates in CMS-authored templates.
//
// A CMS template resolves `{{token}}`s against whatever data the route loaded.
// When a token has no value it resolves to "", which previously shipped as
// `"name": ""` / `"startDate": ""` in the published JSON-LD — an ERROR in
// Google's Rich Results Test, where an *absent* property is only a warning.
// Emitting nothing is always at least as good as emitting a blank.
//
// `0` and `false` are real values and must survive: a free event's
// `"price": 0` is meaningful data, not a missing field.
export const isEmptyValue = (value) =>
  value === "" ||
  value === null ||
  value === undefined ||
  (Array.isArray(value) && value.length === 0) ||
  (typeof value === "object" &&
    !Array.isArray(value) &&
    Object.keys(value).length === 0);

export const pruneEmpty = (node) => {
  if (Array.isArray(node)) {
    return node.map(pruneEmpty).filter((item) => !isEmptyValue(item));
  }

  if (node && typeof node === "object") {
    const result = {};
    for (const [key, value] of Object.entries(node)) {
      const pruned = pruneEmpty(value);
      if (!isEmptyValue(pruned)) result[key] = pruned;
    }
    return result;
  }

  return node;
};

const typesOf = (node) => {
  const type = node?.["@type"];
  if (!type) return [];
  return (Array.isArray(type) ? type : [type]).filter(
    (t) => typeof t === "string",
  );
};

// schema.org Event subtypes all end in "Event" (MusicEvent, TheaterEvent,
// ComedyEvent, ...). EventSeries and EventReservation are different things
// with different required properties, so they're left alone.
const NON_EVENT_TYPES = new Set(["EventSeries", "EventReservation"]);

export const isEventNode = (node) =>
  typesOf(node).some(
    (type) => type.endsWith("Event") && !NON_EVENT_TYPES.has(type),
  );

// Google requires Event.startDate to be ISO 8601. A CMS template that writes
// `{{event_start_date}}` gets the human-readable "27 Sep 2026" form, which is
// a present-but-invalid value — no better to Google than a blank, and harder
// to notice. Accepts a plain date or a date-time with an explicit Z/offset.
const ISO_8601 =
  /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:?\d{2})?)?$/;

export const isIso8601 = (value) => {
  if (typeof value !== "string" || !ISO_8601.test(value.trim())) return false;
  return !Number.isNaN(new Date(value.trim()).getTime());
};

// Google requires Event.location, and for a physical Place that means an
// address. CMS templates write `"address": "{{venue_address}}"`, which prunes
// away for a venue that's yet to be announced and leaves a bare Place. An
// online event's VirtualLocation needs a url instead.
const hasUsableLocation = (location) =>
  (Array.isArray(location) ? location : [location]).some((loc) => {
    if (!loc || typeof loc !== "object") return false;
    if (typesOf(loc).includes("VirtualLocation")) return Boolean(loc.url);
    return Boolean(loc.address);
  });

// Google requires a `name`, an ISO-8601 `startDate` and a `location` with an
// address on an Event. A node missing or malforming any of them is not a
// rich-result candidate and reports as an error, so it's dropped rather than
// published broken — on an event page that hands the page back to the
// generated schema in lib/schemaGenerator.js, which builds from the real event
// object. Every other node type is left for Google to judge.
export const isPublishableNode = (node) => {
  if (!node || typeof node !== "object") return false;
  if (!isEventNode(node)) return true;
  return (
    Boolean(node.name) &&
    isIso8601(node.startDate) &&
    hasUsableLocation(node.location)
  );
};

const APP_TYPES = new Set([
  "SoftwareApplication",
  "MobileApplication",
  "WebApplication",
]);

// Store listing URL without locale parameters, so the same listing isn't
// published under several addresses.
const cleanStoreUrl = (url) => {
  try {
    const u = new URL(url);
    ["hl", "gl"].forEach((p) => u.searchParams.delete(p));
    return u.toString();
  } catch {
    return null;
  }
};

/**
 * Makes an app node describe only what can be verified.
 *
 * - `aggregateRating` / `review` are removed. The CMS published 4.8 from 500
 *   ratings; the Play Store listing showed 4.9 from 25 (checked 2026-09-29).
 *   Even real store figures can't be used: Google's review-snippet rules
 *   don't allow ratings aggregated from another site.
 * - `operatingSystem`, `installUrl` and `sameAs` come from the store links in
 *   SITE_CONFIG. The CMS said "Android, iOS", but there is no iOS app; iOS
 *   appears as soon as `ios_app_url` is set.
 */
export const normalizeAppNode = (node) => {
  if (!node || typeof node !== "object") return node;
  if (!typesOf(node).some((t) => APP_TYPES.has(t))) return node;

  const { aggregateRating: _rating, review: _review, ...rest } = node;

  const stores = [
    ["Android", cleanStoreUrl(SITE_CONFIG.android_app_url)],
    ["iOS", cleanStoreUrl(SITE_CONFIG.ios_app_url)],
  ].filter(([, url]) => url);

  if (stores.length > 0) {
    rest.operatingSystem = stores.map(([os]) => os).join(", ");
    rest.installUrl = stores[0][1];
    const existing = Array.isArray(rest.sameAs)
      ? rest.sameAs
      : rest.sameAs
        ? [rest.sameAs]
        : [];
    rest.sameAs = [...new Set([...existing, ...stores.map(([, url]) => url)])];
  }

  return rest;
};

/**
 * Prunes empty values from a schema object, drops Event nodes that can't
 * satisfy Google's required fields and normalises app nodes. Returns null
 * when nothing publishable is left, so the caller renders no <script> at all.
 */
export const sanitizeSchema = (schema) => {
  if (!schema || typeof schema !== "object" || Array.isArray(schema)) {
    return null;
  }

  const pruned = pruneEmpty(schema);

  if (Array.isArray(pruned["@graph"])) {
    const graph = pruned["@graph"]
      .filter(isPublishableNode)
      .map(normalizeAppNode);
    if (graph.length === 0) return null;
    return { ...pruned, "@graph": graph, "@context": "https://schema.org" };
  }

  if (!isPublishableNode(pruned)) return null;
  if (Object.keys(pruned).length === 0) return null;

  return { ...normalizeAppNode(pruned), "@context": "https://schema.org" };
};

const parseMaybeJson = (value) => {
  if (!value) return null;
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const collectNodes = (schema) => {
  if (!schema || typeof schema !== "object") return [];
  if (Array.isArray(schema)) return schema.flatMap(collectNodes);
  if (Array.isArray(schema["@graph"])) {
    return [schema, ...schema["@graph"].flatMap(collectNodes)];
  }
  return [schema];
};

/**
 * Answers the one question an event page needs: after resolving its tokens and
 * pruning its blanks, does this CMS-authored schema actually contain a usable
 * Event node?
 *
 * Used to decide whether the page's own generated Event schema would be a
 * duplicate (CMS data is good — stand down) or the only correct one on the
 * page (CMS data resolved to blanks — emit it).
 */
export const cmsSchemaHasUsableEvent = (customSchema, dynamicContext) => {
  const parsed = parseMaybeJson(customSchema);
  if (!parsed) return false;

  const resolved = dynamicContext
    ? resolveDynamicVariables(parsed, dynamicContext)
    : parsed;

  return collectNodes(pruneEmpty(resolved)).some(
    (node) => isEventNode(node) && isPublishableNode(node),
  );
};

/**
 * True when a resolved CMS schema contains a BreadcrumbList that will actually
 * survive publishing with at least one item. Checked after pruning, so a
 * template whose itemListElement resolved to nothing doesn't count — otherwise
 * it would suppress the breadcrumb component's schema and leave the page
 * publishing an empty list instead.
 */
export const schemaHasUsableBreadcrumbList = (schema) =>
  collectNodes(pruneEmpty(schema || {})).some(
    (node) =>
      typesOf(node).includes("BreadcrumbList") &&
      Array.isArray(node.itemListElement) &&
      node.itemListElement.length > 0,
  );
